import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, PictureAsPdf } from '@mui/icons-material';
import {
  exportStaffMedicalPdf,
  getStaffMedicalRecord,
  type MedicalRecordFilters,
} from '@services/medicalRecordsService';

const KIND_OPTIONS = [
  { value: '', label: 'Toate tipurile' },
  { value: 'visit', label: 'Consultații' },
  { value: 'prescription', label: 'Prescripții' },
  { value: 'vaccination', label: 'Vaccinări' },
  { value: 'lab_order', label: 'Laborator' },
  { value: 'document', label: 'Documente' },
  { value: 'visit_addendum', label: 'Addendum' },
  { value: 'prevention', label: 'Prevenție' },
  { value: 'weight', label: 'Greutate' },
  { value: 'soap_note', label: 'Notă SOAP' },
];

const ClinicAnimalMedicalRecordPage: React.FC = () => {
  const { animalId } = useParams<{ animalId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const patientsListPath = location.pathname.startsWith('/doctor/') ? '/doctor/patients' : '/clinic/patients';
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<any>(null);
  const [filters, setFilters] = useState<MedicalRecordFilters>({});
  const [pdfLoading, setPdfLoading] = useState(false);

  const load = useCallback(async () => {
    if (!animalId) return;
    setLoading(true);
    setError('');
    try {
      const d = await getStaffMedicalRecord(animalId, filters);
      setData(d);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Nu s-a putut încărca dosarul.';
      setError(typeof msg === 'string' ? msg : 'Eroare');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [animalId, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const onExportPdf = async () => {
    if (!animalId) return;
    setPdfLoading(true);
    try {
      const blob = await exportStaffMedicalPdf(animalId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dosar-medical-${animalId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Export PDF indisponibil.');
    } finally {
      setPdfLoading(false);
    }
  };

  if (!animalId) {
    return <Alert severity="warning">Lipsește identificatorul animalului.</Alert>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        <Button startIcon={<ArrowBack />} onClick={() => navigate(patientsListPath)}>
          Înapoi la pacienți
        </Button>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
          Dosar medical — {data?.animal?.name || '…'}
        </Typography>
        <Button
          variant="outlined"
          startIcon={pdfLoading ? <CircularProgress size={18} /> : <PictureAsPdf />}
          disabled={pdfLoading || !!error || !data}
          onClick={onExportPdf}
        >
          PDF sumar
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Căutare și filtre cronologie
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <TextField
            size="small"
            label="Căutare text"
            value={filters.q || ''}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value || undefined }))}
          />
          <TextField
            size="small"
            label="De la (ISO)"
            value={filters.from || ''}
            onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value || undefined }))}
          />
          <TextField
            size="small"
            label="Până la (ISO)"
            value={filters.to || ''}
            onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value || undefined }))}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Tip eveniment</InputLabel>
            <Select
              label="Tip eveniment"
              value={filters.kind || ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, kind: (e.target.value as string) || undefined }))
              }
            >
              {KIND_OPTIONS.map((o) => (
                <MenuItem key={o.value || 'all'} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={() => load()} disabled={loading}>
            Aplică
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {data.animal?.species} {data.animal?.breed ? `· ${data.animal.breed}` : ''}
              {data.animal?.weight != null ? ` · ${data.animal.weight} kg` : ''}
            </Typography>
          </Paper>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tab label="Cronologie" />
            <Tab label="Consultații" />
            <Tab label="Prescripții" />
            <Tab label="Vaccinări" />
            <Tab label="Laborator" />
            <Tab label="Documente" />
            <Tab label="Addendum / prevenție / SOAP" />
          </Tabs>

          {tab === 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Titlu</TableCell>
                  <TableCell>Rezumat</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.timeline || []).map((row: any) => (
                  <TableRow key={row.id}>
                    <TableCell>{new Date(row.at).toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.kind} />
                    </TableCell>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>{row.summary || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {tab === 1 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Diagnostic</TableCell>
                  <TableCell>Recomandări</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.visits || []).map((v: any) => (
                  <TableRow key={v.id}>
                    <TableCell>{v.visit_date}</TableCell>
                    <TableCell>{v.visit_type}</TableCell>
                    <TableCell>{v.status}</TableCell>
                    <TableCell>{v.diagnosis || '—'}</TableCell>
                    <TableCell>{v.recommendations || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {tab === 2 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status afișat</TableCell>
                  <TableCell>Emisă</TableCell>
                  <TableCell>Medic</TableCell>
                  <TableCell>Poziții</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.prescriptions || []).map((p: any) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.display_status || p.status}</TableCell>
                    <TableCell>{p.created_at || p.issued_at || '—'}</TableCell>
                    <TableCell>{p.veterinarian_name || '—'}</TableCell>
                    <TableCell>
                      {(p.items || [])
                        .map((it: any) => it.product_name || it.medicine_name || it.name || it.label)
                        .filter(Boolean)
                        .join(', ') || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {tab === 3 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Vaccin</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.vaccinations || []).map((x: any) => (
                  <TableRow key={x.id}>
                    <TableCell>{x.vaccine_name}</TableCell>
                    <TableCell>{x.administered_date}</TableCell>
                    <TableCell>{x.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {tab === 4 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Comandă</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.lab_orders || []).map((x: any) => (
                  <TableRow key={x.id}>
                    <TableCell>{x.lab_provider || x.id}</TableCell>
                    <TableCell>{x.status}</TableCell>
                    <TableCell>{x.received_at || x.ordered_at}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {tab === 5 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fișier</TableCell>
                  <TableCell>Categorie</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Încărcat</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.documents || []).map((d: any) => (
                  <TableRow key={d.id}>
                    <TableCell>{d.original_filename || d.filename}</TableCell>
                    <TableCell>{d.category || '—'}</TableCell>
                    <TableCell>{d.is_visible_to_client ? 'Da' : 'Nu'}</TableCell>
                    <TableCell>{d.uploaded_at}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {tab === 6 && (
            <Box>
              <Typography variant="subtitle2">Addendum consultații</Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Vizită</TableCell>
                    <TableCell>Conținut (truncat)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.visit_addenda || []).map((a: any) => (
                    <TableRow key={a.id}>
                      <TableCell>{a.created_at}</TableCell>
                      <TableCell>{a.visit_id}</TableCell>
                      <TableCell>{String(a.content || a.reason || '').slice(0, 200)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2">Prevenție / scadențe</Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableBody>
                  {(data.prevention || []).map((p: any) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.due_at}</TableCell>
                      <TableCell>{p.status}</TableCell>
                      <TableCell>{p.notes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="subtitle2">Note SOAP (metadate)</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Vizită</TableCell>
                    <TableCell>Actualizat</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.soap_notes || []).map((s: any) => (
                    <TableRow key={s.id}>
                      <TableCell>{s.status}</TableCell>
                      <TableCell>{s.visit_id}</TableCell>
                      <TableCell>{s.updated_at || s.created_at}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      ) : null}
    </Box>
  );
};

export default ClinicAnimalMedicalRecordPage;
